// MACHINERY V2 (Stroje) — the BOTTOM SHEET primitive (M2.16a).
//
// Spec: mobile/README.md §Standards ("Sheets: radius 16 top, drag handle 36x4,
// backdrop rgba(15,23,30,0.32) + blur(2px), footer buttons h 46-48") and the
// rendered truth mobile/screenshots/02-filter-sheet-open.png. Reference impl:
// mobile/machinery-mobile.jsx MbFilterSheet (:126-164) — VALUES taken, code not.
//
// THREE CONSUMERS BY DESIGN, which is why it is a kit primitive and not part of
// the fleet: the fleet filter sheet (16a), the katalog sheets (16c) and the
// profile's mobile surfaces (16d). Anything true of ALL of them lives here;
// anything fleet-specific lives in fleet/FleetFilterSheet.tsx.
//
// ============================================================
//  THE LAYOUT IS SPECIFIED, NOT DISCOVERED
// ============================================================
// Flex column. Handle, header and footer are `flexShrink: 0`; the body is
// `overflow: auto; flex: 1; minHeight: 0`. Pinned chrome, scrolling middle. The
// fleet's own sheet has SIX sections and WILL scroll, so a body that grew the
// sheet instead of scrolling would push the footer — and therefore the primary
// action — off the bottom of the screen with no way to reach it.
//
// ============================================================
//  PORTAL RULE — REUSED, NOT RE-DERIVED
// ============================================================
// Radix Dialog.Content portals to document.body, OUTSIDE `.machinery-v2-root`,
// where --card / --border / --ink / --mod-accent do not resolve; a portalled
// node styled with them paints unthemed. The countermeasure is the one
// profile/tabs/TabDialog.tsx:98-113 already ships (personally re-read at commit
// time), followed exactly:
//   · Content re-carries `machinery-v2-root p-<palette> mod-stroje`;
//   · Content sets inline background:"transparent", because re-carrying the root
//     class also re-applies its opaque `background: var(--bg)`, which would slab
//     the whole screen behind the scrim;
//   · the real surface is painted by the INNER div;
//   · `palette` is read from the slice LIVE on every render, never captured at
//     mount, or a sheet opened before a palette flip keeps the old palette;
//   · the Overlay deliberately does NOT carry the scope class — it is a fixed
//     rgba scrim and classing it would repaint var(--bg).
//
// ============================================================
//  STOP PROPAGATION ON BOTH PORTALLED SIBLINGS, MOUSE AND KEYBOARD
// ============================================================
// Radix splits the portal into Overlay AND Content, and React synthetic events
// bubble the COMPONENT tree back into whatever rendered the sheet — so guarding
// one leaks backdrop clicks into the opener. Both are guarded here, ONCE, for
// every consumer: the module has had four separate defects where an interactive
// control's activation reached a navigating host (FleetCardMenu.tsx:61-94 holds
// the full history), and the lesson recorded there is to fix propagation
// centrally rather than per call site.
//
// stopPropagation ONLY — NEVER preventDefault: the browser synthesises a
// button's click FROM its key event, so preventing the default would kill the
// control. Esc still closes: Radix listens for it with a DOCUMENT-LEVEL CAPTURE
// listener (@radix-ui/react-use-escape-keydown/dist/index.js, `addEventListener
// ("keydown", handleKeyDown, { capture: true })`, personally read at commit
// time), which fires before the event reaches this subtree and cannot be
// stopped from inside it. Backdrop close likewise runs off Radix's own
// outside-pointer detection, not off this onClick — the identical guard in
// TabDialog has shipped since M2.5 with both behaviours intact.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";

export interface SheetV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sheet heading. Also the dialog's accessible name (Dialog.Title). */
  title: string;
  /** Optional right-aligned text action in the header row (the fleet sheet's
   *  "Zrusit vsetko"). */
  headerAction?: React.ReactNode;
  /** Pinned footer row. Buttons are h46-48 per the Standards block. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const SheetV2: React.FC<SheetV2Props> = ({
  open,
  onOpenChange,
  title,
  headerAction,
  footer,
  children,
}) => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  const swallowKeys = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          onClick={(e) => e.stopPropagation()}
          onKeyDown={swallowKeys}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,30,0.32)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            zIndex: 1300,
          }}
        />
        <Dialog.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={swallowKeys}
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            // 1300 overlay / 1301 content is the v2 dialog tier. The design's
            // own `position:absolute; zIndex:30` (machinery-mobile.jsx:129) is
            // NOT copied — that value exists only because the mock renders
            // inside an artboard frame. A menu opened INSIDE a sheet takes 1400.
            zIndex: 1301,
            display: "flex",
            // Percentages on a fixed box resolve against the viewport, so this
            // is the design's own `maxHeight: "78%"` (machinery-mobile.jsx:131)
            // meaning exactly what it means there.
            maxHeight: "78%",
            background: "transparent",
          }}
        >
          <div
            className="mv2-sheet"
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              background: "var(--card)",
              borderRadius: "16px 16px 0 0",
              boxShadow: "0 -8px 32px rgba(15,23,30,0.18)",
              padding: "8px 18px 16px",
              // Progressive enhancement, in this order on purpose: if env() is
              // unsupported the declaration is dropped and the shorthand's 16px
              // above still stands. A notched phone would otherwise put the
              // primary action under the home indicator.
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: "var(--border)",
                margin: "4px auto 10px",
                flexShrink: 0,
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
                paddingBottom: 8,
                minWidth: 0,
              }}
            >
              <Dialog.Title asChild>
                <span
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 15.5,
                    fontWeight: 700,
                    color: "var(--ink)",
                    minWidth: 0,
                  }}
                >
                  {title}
                </span>
              </Dialog.Title>
              <span style={{ flex: 1 }} />
              {headerAction}
            </div>

            <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>{children}</div>

            {footer && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  paddingTop: 12,
                  flexShrink: 0,
                  borderTop: "1px solid var(--border-soft)",
                }}
              >
                {footer}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SheetV2;
